import { combineEpics, ofType } from 'redux-observable';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS,
} from 'applications/scribe/instrumentations/track';
import _get from 'lodash/get';
import { EMPTY } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { userInfoSelector } from 'selectors/app-selector';
import {
  bulkUploadDeliveriesSelector,
  bulkUploadGroupingClusterMappingsSelector,
  bulkUploadGroupingTrialDeliveriesSelector,
} from 'selectors/new-bulk-upload-selectors';
import { calculateGroupingOptimizedAmount } from 'helper/new-bulk-booking';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck.js';
import { appliedOfferResult, isAppliedOffer } from './helper/scribe-helper';

const scribeExpressNewBulkUploadCommonEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      // STEP1
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_WEB_DOWNLOAD_TEMPLATE,
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_WEB_UPLOAD,
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_WEB_BACK,
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_WEB_SUCCESS_UPLOAD,

      // STEP2
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_REVIEW_WEB_SELECT_ALL_ROWS,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_REVIEW_WEB_SELECT_ROWS_WITH_ERROR,
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_REVIEW_WEB_EDIT_ADDRESS,
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_REVIEW_WEB_CONFIRM,

      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB_LOCATION_NOTES,
    ),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      const facets = getCommonFacet(userInfo);
      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const bulkUploadStepToStateNames = {
  1: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_WEB,
  2: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_REVIEW_WEB,
  3: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB,
  4: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_BOOK_WEB,
};

const scribeBulkUploadNavigateStepEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_NAVIGATE_STEP),
    mergeMap(action => {
      const { currentStep, toStep } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName: bulkUploadStepToStateNames[currentStep],
        eventName: `BACK_TO_STEP_${toStep}`,
        facets: getCommonFacet(userInfo),
      });
      return EMPTY;
    }),
  );

const scribeExpressNewBulkUploadErrorPageCommon = (action$, state$) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_ERROR_PAGE_DEFAULT,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_ERROR_PAGE_CONFIRM_INVALID_FIELD,
    ),
    mergeMap(action => {
      const { stateName, eventName, errorCode } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_ERROR_TYPE]: errorCode,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressNewBulkUploadStep2DefaultEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_REVIEW_WEB_DEFAULT,
    ),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        totalRow,
        errorCode,
        districtUsage,
        totalAddressCount,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_TOTAL]: totalRow,
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_ERROR_CODE]: errorCode,
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_DISTRICT_USAGE]: districtUsage || 0,
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_TOTAL_ADDR_COUNT]:
            totalAddressCount || 0,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressNewBulkUploadStep2EditAddressDrawerDefaultEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB_DEFAULT,
    ),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        poiType,
        originAddress,
        adjustedAddress,
        adminArea,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.POI_TYPE]: poiType,
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ORIGINAL]: originAddress,
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ADJUSTED]: adjustedAddress,
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_DISTRICT_PATH]: _get(
            adminArea,
            'path',
          ),
          [SCRIBE_EVENT_PARAMS.BULK_UPLOAD_DISTRICT_SELECTION]: _get(
            adminArea,
            'name',
          ),
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressNewBulkUploadStep2EditAddressDrawerAddressEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB_ADDRESS,
    ),
    mergeMap(action => {
      const { stateName, eventName, adjustedAddress } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ADJUSTED]: adjustedAddress,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressNewBulkUploadStep2EditAddressDrawerCancelEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB_CANCEL,
    ),
    mergeMap(action => {
      const { stateName, eventName, originAddress } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ORIGINAL]: originAddress,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressNewBulkUploadStep2EditAddressDrawerConfirmEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB_CONFIRM,
    ),
    mergeMap(action => {
      const { stateName, eventName, originAddress, adjustedAddress } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ORIGINAL]: originAddress,
          [SCRIBE_EVENT_PARAMS.BULK_EDIT_ADJUSTED]: adjustedAddress,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBukUploadSetupDeliveryDefaultEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_DEFAULT,
    ),
    mergeMap(action => {
      const { stateName, eventName, scribeSetupDeliveriesCommonData } = action;
      const {
        totalSenders,
        deliveriesBySender,
        totalDeliveries,
      } = scribeSetupDeliveriesCommonData;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_SENDER_TOTAL]: totalSenders,
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_SENDER]: deliveriesBySender,
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_TOTAL]: totalDeliveries,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBukUploadSetupDeliveryConfirmEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_CONFIRM,
    ),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        scribeSetupDeliveriesCommonData,
        scribeFare,
      } = action;
      const {
        totalSenders,
        deliveriesBySender,
        totalDeliveries,
      } = scribeSetupDeliveriesCommonData;
      const { currencyCode, amount } = scribeFare;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.BULK_SENDER_TOTAL]: totalSenders,
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_SENDER]: deliveriesBySender,
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_TOTAL]: totalDeliveries,
          [SCRIBE_EVENT_PARAMS.CURRENCY]: currencyCode,
          [SCRIBE_EVENT_PARAMS.FARE]: amount,
        },
      });
      return EMPTY;
    }),
  );

const getGroupingOptimizedData = state => {
  const allDeliveries = bulkUploadDeliveriesSelector(state);
  const trialDeliveries = bulkUploadGroupingTrialDeliveriesSelector(state);
  const clusterMappings = bulkUploadGroupingClusterMappingsSelector(state);
  const {
    currencyCode,
    originalAmount,
    originalDeliveryCount,
    optimisedAmount,
    optimisedDeliveryCount,
  } = calculateGroupingOptimizedAmount({
    allDeliveries,
    trialDeliveries,
    clusterMappings,
  });
  return {
    [SCRIBE_EVENT_PARAMS.CURRENCY]: currencyCode,
    [SCRIBE_EVENT_PARAMS.ORIGINAL_AMOUNT]: originalAmount,
    [SCRIBE_EVENT_PARAMS.ORIGINAL_COUNT]: originalDeliveryCount,
    [SCRIBE_EVENT_PARAMS.OPTIMIZED_AMOUNT]: optimisedAmount,
    [SCRIBE_EVENT_PARAMS.OPTIMIZED_COUNT]: optimisedDeliveryCount,
  };
};

const scribeExpressBukUploadSetupDeliveryToggleMultiStopEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_TOGGLE_MULTI_STOP,
    ),
    mergeMap(action => {
      const { isGrouped } = action;

      const eventName = isGrouped
        ? SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS.TOGGLE_MULTI_STOP_ON
        : SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS.TOGGLE_MULTI_STOP_OFF;

      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          ...getGroupingOptimizedData(state$.value),
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBukUploadSetupDeliveryReviewMultiStopEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_REVIEW_MULTI_STOP,
    ),
    mergeMap(action => {
      const { cta, doNotShowAgain } = action;

      const eventName =
        cta === undefined
          ? SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS.REVIEW_MULTI_STOP
          : cta === 'skip'
            ? SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS.REVIEW_MULTI_STOP_SKIP
            : SCRIBE_EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EVENTS.REVIEW_MULTI_STOP_ACCEPT;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          ...(doNotShowAgain !== undefined && {
            [SCRIBE_EVENT_PARAMS.DO_NOT_SHOW_AGAIN]: doNotShowAgain,
          }),
          ...getGroupingOptimizedData(state$.value),
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBukUploadSetupDeliveryCommonEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_SELECT_ALL,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EDIT_SERVICE_TYPE,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EDIT_VEHICLE_TYPE,
      scribeNewBulkUploadDuck.types
        .EXPRESS_BULK_UPLOAD_SETUP_DELIVERY_WEB_EDIT_ADD_ON,
    ),
    mergeMap(action => {
      const { stateName, eventName } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBulkUploadBookDefault = (action$, state$) =>
  action$.pipe(
    ofType(scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_BOOK_WEB_DEFAULT),
    mergeMap(action => {
      const { stateName, eventName, fare, code, totalDeliveries } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.FARE]: fare,
          [SCRIBE_EVENT_PARAMS.CURRENCY]: code,
          [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_TOTAL]: totalDeliveries,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBulkUploadBookPaymentMethod = (action$, state$) =>
  action$.pipe(
    ofType(
      scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_BOOK_WEB_PAYMENT_METHOD,
    ),
    mergeMap(action => {
      const { stateName, eventName, paymentMethod, fare } = action;
      const userInfo = userInfoSelector(state$.value);
      scribeTrackEvent({
        stateName,
        eventName,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.FARE]: fare,
          [SCRIBE_EVENT_PARAMS.PAYMENT_METHOD]: paymentMethod,
        },
      });
      return EMPTY;
    }),
  );

const scribeExpressBulkUploadBookOffers = (action$, state$) =>
  action$.pipe(
    ofType(scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_BOOK_WEB_OFFERS),
    mergeMap(action => {
      const { stateName, eventName, selectedOffer, fare } = action;
      const userInfo = userInfoSelector(state$.value);
      const facets = {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.FARE]: fare,
        [SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER]: isAppliedOffer(selectedOffer),
      };
      if (selectedOffer) {
        facets[SCRIBE_EVENT_PARAMS.APPLIED_OFFER_RESULT] = appliedOfferResult(
          selectedOffer,
        );
      }
      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

const scribeExpressBulkUploadBookBook = (action$, state$) =>
  action$.pipe(
    ofType(scribeNewBulkUploadDuck.types.EXPRESS_BULK_UPLOAD_BOOK_WEB_BOOK),
    mergeMap(action => {
      const {
        stateName,
        eventName,
        selectedOffer,
        fare,
        code,
        totalDeliveries,
        uploadedDistrict,
      } = action;
      const userInfo = userInfoSelector(state$.value);
      const facets = {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.FARE]: fare,
        [SCRIBE_EVENT_PARAMS.CURRENCY]: code,
        [SCRIBE_EVENT_PARAMS.IS_APPLIED_OFFER]: isAppliedOffer(selectedOffer),
        [SCRIBE_EVENT_PARAMS.BULK_DELIVERY_TOTAL]: totalDeliveries,
        [SCRIBE_EVENT_PARAMS.UPLOADED_DISTRICT]: uploadedDistrict,
      };
      if (selectedOffer) {
        facets[SCRIBE_EVENT_PARAMS.APPLIED_OFFER_RESULT] = appliedOfferResult(
          selectedOffer,
        );
      }
      scribeTrackEvent({
        stateName,
        eventName,
        facets,
      });
      return EMPTY;
    }),
  );

export default combineEpics(
  scribeExpressNewBulkUploadCommonEpic,
  scribeBulkUploadNavigateStepEpic,
  scribeExpressNewBulkUploadStep2DefaultEpic,
  scribeExpressNewBulkUploadStep2EditAddressDrawerDefaultEpic,
  scribeExpressNewBulkUploadStep2EditAddressDrawerAddressEpic,
  scribeExpressNewBulkUploadStep2EditAddressDrawerCancelEpic,
  scribeExpressNewBulkUploadStep2EditAddressDrawerConfirmEpic,
  scribeExpressBukUploadSetupDeliveryDefaultEpic,
  scribeExpressBukUploadSetupDeliveryConfirmEpic,
  scribeExpressBukUploadSetupDeliveryToggleMultiStopEpic,
  scribeExpressBukUploadSetupDeliveryReviewMultiStopEpic,
  scribeExpressBukUploadSetupDeliveryCommonEpic,
  scribeExpressNewBulkUploadErrorPageCommon,
  scribeExpressBulkUploadBookDefault,
  scribeExpressBulkUploadBookPaymentMethod,
  scribeExpressBulkUploadBookOffers,
  scribeExpressBulkUploadBookBook,
);
